import React, { useState } from 'react';
import { UserCardV1 } from '../../../../features/User/UserCard';
import { useTemplate } from '../../../../features/template/TemplateProvider';
import useApi from '../../../../hook/useApi';
import { ButtonCancel, ButtonPrimary } from '../../../../utils/Button';
import { GetDate, GetTime } from '../../../../utils/Show';
import cn from '../../../../utils/cn';

function ModalRenewService() {
    const { modalData, closeModal, setMessage } = useTemplate();
    const { put } = useApi();
    const [loading, setLoading] = useState(false);
    console.log(modalData);
    // Sample data structure based on provided JSON
    const serviceData = modalData || {};

    const handleCreateInvoice = async () => {
        try {
            setLoading(true);
            const { error, message } = await put(`/api/purchase/service/${serviceData.id}/renew`);
            if (error) {
                throw new Error(message);
            }
            setMessage({ type: 'success', text: message });
            closeModal();
        } catch (error) {
            setMessage({ type: 'error', text: error.message });
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusStyles = {
            completed: 'bg-success-100 text-success-700',
            ongoing: 'bg-warning-100 text-warning-700',
            pending: 'bg-info-100 text-info-700',
            cancelled: 'bg-error-100 text-error-700'
        };

        return (
            <span
                className={cn(
                    'px-2 py-1 rounded-full text-xs font-medium capitalize',
                    statusStyles[status] || 'bg-gray-100 text-gray-700'
                )}
            >
                {status}
            </span>
        );
    };

    return (
        <div className=" w-full">
            {/* Customer Information */}
            <div className="mb-3">
                <div className="bg-secondary-50 rounded-lg p-4">
                    <UserCardV1
                        title={serviceData?.user?.fullName}
                        subTitle={serviceData?.user?.organization}
                        className="mb-3"
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Email
                            </label>
                            <p className="text-sm text-primary">
                                {serviceData?.user?.primaryEmail}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Service Information */}
            <div className="mb-3">
                <h3 className="text-lg font-medium text-primary mb-3">Service Information</h3>
                <div className="bg-secondary-50 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Service ID
                            </label>
                            <p className="text-sm text-primary font-medium">#{serviceData.id}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Service Name
                            </label>
                            <p className="text-sm text-primary font-medium">
                                {serviceData.serviceName}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Product Type
                            </label>
                            <p className="text-sm text-primary capitalize">
                                {serviceData.productType}
                            </p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Status
                            </label>
                            <div>{getStatusBadge(serviceData.status)}</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Application ID
                            </label>
                            <p className="text-sm text-primary">#{serviceData.applicationId}</p>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Subscription ID
                            </label>
                            <p className="text-sm text-primary">
                                {serviceData.subscriptionId
                                    ? `#${serviceData.subscriptionId}`
                                    : 'N/A'}
                            </p>
                        </div>
                    </div>

                    {/* Timeline Information */}
                    <div className="mt-4 pt-4 border-t border-secondary-200">
                        <h4 className="text-sm font-medium text-primary-700 mb-2">Timeline</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                            <div>
                                <span className="text-primary-700">Initiated:</span>
                                <span className="ml-2 text-primary">
                                    {serviceData.timeInitiate
                                        ? GetDate(serviceData.timeInitiate)
                                        : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-primary-700">Implemented:</span>
                                <span className="ml-2 text-primary">
                                    {serviceData.timeImplement
                                        ? GetDate(serviceData.timeImplement)
                                        : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-primary-700">Ongoing:</span>
                                <span className="ml-2 text-primary">
                                    {serviceData.timeOngoing
                                        ? GetDate(serviceData.timeOngoing)
                                        : 'N/A'}
                                </span>
                            </div>
                            <div>
                                <span className="text-primary-700">Completed:</span>
                                <span className="ml-2 text-primary">
                                    {serviceData.timeDone ? GetDate(serviceData.timeDone) : 'N/A'}
                                </span>
                            </div>
                        </div>
                        <div className="mt-2">
                            <span className="text-primary-700 text-xs">Last Updated:</span>
                            <span className="ml-2 text-primary text-xs">
                                {serviceData.timeUpdated
                                    ? `${GetDate(serviceData.timeUpdated)} ${GetTime(serviceData.timeUpdated)}`
                                    : 'N/A'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Package Information */}
            {serviceData.packageName && (
                <div className="mb-6">
                    <h3 className="text-lg font-medium text-primary mb-3">Package Information</h3>
                    <div className="bg-secondary-50 rounded-lg p-4">
                        <div>
                            <label className="block text-sm font-medium text-primary-700 mb-1">
                                Package Name
                            </label>
                            <p className="text-sm text-primary">{serviceData.packageName}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-secondary-200">
                <ButtonCancel title="Cancel" onClick={() => closeModal()} disabled={loading} />
                <ButtonPrimary
                    title={loading ? 'Renewing Invoice...' : 'Renew Invoice'}
                    onClick={handleCreateInvoice}
                    loading={loading}
                    disabled={loading}
                />
            </div>
        </div>
    );
}

export default ModalRenewService;
